import QRCode from "qrcode";

export type PrintBrandedQROptions = {
  uid: string;
  itemName?: string;
  bottomText?: string;
  bottomText2?: string;
  bottomText3?: string;
  logoBase64?: string;
  eventName?: string;
  accessType?: string;
  tokenBalance?: string;
  printedBy?: string;
};

const safeStr = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v));

function toDataUrlFromBase64(b64?: string): string | null {
  if (!b64) return null;
  const trimmed = b64.trim();
  if (trimmed.startsWith("data:image/")) return trimmed;
  return `data:image/png;base64,${trimmed}`;
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  if (!text) return [];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
    } else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

export async function generatePrintBrandedQRDataUrl(opts: PrintBrandedQROptions): Promise<string> {
  const {
    uid,
    eventName = "Event Name",
    accessType = "General Admission",
    tokenBalance,
    printedBy,
    logoBase64,
  } = opts;

  // 2" x 5.5" at 300 DPI = 600 x 1650 pixels
  const canvasWidth = 600;
  const canvasHeight = 1650;
  const qrSize = 500; // Larger QR code
  const logoSize = 100;
  const padding = 30;

  // Tear-off section is 1 inch = 300 pixels at 300 DPI
  const tearOffHeight = 300;

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d")!;

  // Background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // ============================================
  // TEAR-OFF SECTION (Top 1 inch - UPSIDE DOWN)
  // ============================================
  ctx.save();

  // Rotate 180 degrees around the center of the tear-off section
  ctx.translate(canvasWidth / 2, tearOffHeight / 2);
  ctx.rotate(Math.PI);
  ctx.translate(-canvasWidth / 2, -tearOffHeight / 2);

  // UID in tear-off section - FULL BLACK - LARGER
  ctx.fillStyle = "#000000";
  ctx.font = "700 48px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(`UID: ${uid}`, canvasWidth / 2, 40);

  // Token balance in tear-off section - FULL BLACK - LARGER with wrapping
  if (tokenBalance) {
    ctx.font = "600 34px system-ui, -apple-system, Segoe UI, Roboto, Arial";
    ctx.fillText(`Balance:`, canvasWidth / 2, 120);

    // Wrap token balance to fit within tear-off section
    ctx.font = "600 30px system-ui, -apple-system, Segoe UI, Roboto, Arial";
    const tearOffBalanceLines = wrapText(ctx, tokenBalance, canvasWidth - 60);
    let tearOffY = 160;
    for (const line of tearOffBalanceLines) {
      ctx.fillText(line, canvasWidth / 2, tearOffY);
      tearOffY += 32;
    }
  }

  ctx.restore();

  // Draw perforated tear line
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 2;
  ctx.setLineDash([10, 5]);
  ctx.beginPath();
  ctx.moveTo(0, tearOffHeight);
  ctx.lineTo(canvasWidth, tearOffHeight);
  ctx.stroke();
  ctx.setLineDash([]);

  // ============================================
  // CREATE TEXT CANVAS (to be rotated later)
  // ============================================

  // Calculate available space between tear-off and QR
  const qrBottomMargin = 40;
  const qrY = canvasHeight - qrSize - qrBottomMargin;
  const availableTextHeight = qrY - tearOffHeight - 80;

  // Create text canvas with max width = available height on final ticket
  const textCanvas = document.createElement("canvas");
  textCanvas.width = availableTextHeight;
  textCanvas.height = canvasWidth; // Will be rotated, so height = ticket width
  const textCtx = textCanvas.getContext("2d")!;

  // White background
  textCtx.fillStyle = "#FFFFFF";
  textCtx.fillRect(0, 0, textCanvas.width, textCanvas.height);

  // Draw text normally (will be rotated later)
  textCtx.fillStyle = "#000000";
  textCtx.textAlign = "center";
  textCtx.textBaseline = "top";

  let y = 35; // Start position from top
  const centerX = textCanvas.width / 2;

  // Event Name - Large, bold, full black
  textCtx.font = "700 64px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  const eventLines = wrapText(textCtx, safeStr(eventName), textCanvas.width - 40);
  for (const line of eventLines) {
    textCtx.fillText(line, centerX, y);
    y += 70;
  }

  y += 45;

  // Access Type - Full black
  textCtx.font = "700 50px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  const accessLines = wrapText(textCtx, safeStr(accessType), textCanvas.width - 40);
  for (const line of accessLines) {
    textCtx.fillText(line, centerX, y);
    y += 48;
  }

  y += 45;

  // Token Balance - Full black
  if (tokenBalance) {
    textCtx.font = "600 40px system-ui, -apple-system, Segoe UI, Roboto, Arial";
    textCtx.fillText(`Balance:`, centerX, y);
    y += 40;

    const balanceLines = wrapText(textCtx, tokenBalance, textCanvas.width - 40);
    for (const line of balanceLines) {
      textCtx.fillText(line, centerX, y);
      y += 38;
    }
    y += 25;
  }

  // UID - Full black
  textCtx.font = "600 40px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  textCtx.fillText(`UID: ${uid}`, centerX, y);
  y += 55;

  // Calculate footer height and position at bottom (flex-end style)
  const printedDate = new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  textCtx.font = "500 30px system-ui, -apple-system, Segoe UI, Roboto, Arial";

  const footerHeight = printedBy ? 60 : 30; // 30px for printed date, +30 if printedBy exists
  const footerY = textCanvas.height - footerHeight - 10; // Tighter bottom padding (10px instead of 40px)

  // Printed On date - Full black - positioned at bottom
  textCtx.fillText(`Printed: ${printedDate}`, centerX, footerY);

  // Printed By (if provided) - Full black
  if (printedBy) {
    textCtx.fillText(`By: ${printedBy}`, centerX, footerY + 30);
  }

  // ============================================
  // CREATE QR CODE CANVAS
  // ============================================

  const qrCanvas = document.createElement("canvas");
  qrCanvas.width = qrSize;
  qrCanvas.height = qrSize;
  await QRCode.toCanvas(qrCanvas, safeStr(uid), {
    errorCorrectionLevel: "H",
    margin: 1,
    width: qrSize,
    color: { dark: "#000000", light: "#FFFFFF" },
  });

  // Add logo over QR if provided
  const logoDataUrl = toDataUrlFromBase64(logoBase64);
  if (logoDataUrl) {
    const img = await new Promise<HTMLImageElement | null>((resolve) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => resolve(null);
      i.src = logoDataUrl;
    });

    if (img) {
      const qrCtx = qrCanvas.getContext("2d")!;
      const ratio = Math.min(logoSize / img.width, logoSize / img.height, 1);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);

      const logoX = Math.round(qrSize / 2 - w / 2);
      const logoY = Math.round(qrSize / 2 - h / 2);

      const pad = 8;
      qrCtx.fillStyle = "#FFFFFF";
      drawRoundedRect(qrCtx, logoX - pad, logoY - pad, w + pad * 2, h + pad * 2, 12);
      qrCtx.fill();

      qrCtx.drawImage(img, logoX, logoY, w, h);
    }
  }

  // ============================================
  // COMPOSE ALL THREE SECTIONS
  // ============================================

  // Draw rotated text canvas in the middle section
  ctx.save();
  const textX = canvasWidth / 2;
  const textY = tearOffHeight + availableTextHeight / 2;
  ctx.translate(textX, textY);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(textCanvas, -textCanvas.width / 2, -textCanvas.height / 2);
  ctx.restore();

  // Draw rotated QR code at the bottom
  ctx.save();
  const qrX = canvasWidth / 2;
  ctx.translate(qrX, qrY + qrSize / 2);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(qrCanvas, -qrSize / 2, -qrSize / 2);
  ctx.restore();

  // ============================================
  // ROTATE ENTIRE IMAGE 180 DEGREES
  // ============================================
  // Create a new canvas to hold the rotated image
  const rotatedCanvas = document.createElement("canvas");
  rotatedCanvas.width = canvasWidth;
  rotatedCanvas.height = canvasHeight;
  const rotatedCtx = rotatedCanvas.getContext("2d")!;

  // Rotate 180 degrees around the center
  rotatedCtx.translate(canvasWidth / 2, canvasHeight / 2);
  rotatedCtx.rotate(Math.PI);
  rotatedCtx.drawImage(canvas, -canvasWidth / 2, -canvasHeight / 2);

  return rotatedCanvas.toDataURL("image/png");
  // return canvas.toDataURL("image/png");
}

export async function printBrandedQR(opts: PrintBrandedQROptions): Promise<void> {
  const dataUrl = await generatePrintBrandedQRDataUrl(opts);

  // Open a new window with the image for printing
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    throw new Error("Could not open print window. Please allow pop-ups for this site.");
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print Ticket QR</title>
        <style>
          @page {
            size: 2in 5.5in;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 2in;
            height: 5.5in;
          }
          img {
            width: 2in;
            height: 5.5in;
            display: block;
          }
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <img src="${dataUrl}" alt="Ticket QR" />
        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
}